import { AxiosResponse } from 'axios';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import Collapsible from 'components/collapsible';
import TextField from 'components/text-field';

import LoadingSpinner from 'components/loading-spinner';
import { usePost } from 'hooks/use-post';
import ActionButtons from 'components/action-buttons';
import { useSiteSettingsState } from 'views/site-settings/context/site-settings-context';
import { putSiteComment } from '../api/silo-management';
import { PutSiteComment } from '../types/put-site-comment.type';
import { useAccountState } from 'contexts/account/account-context';
import { useUserFeatures } from 'views/site-settings/hooks/use-user-features';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';

type ParamTypes = {
  globalSiteId: string;
};

type Props = {};

const PAGE_NAME_ID = 'siteSettings.editSiteSettings.siteComment';

const SiteInformation: React.FC<Props> = ({}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();
  const { canEditSiteSettings } = useUserFeatures();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    siteSettingsQuery: { data: settings, isLoading },
  } = useSiteSettingsState();

  if (isLoading) return <LoadingSpinner />; // bad solution?
  if (!settings) return <></>;

  //#region STATE
  const [edit, setEdit] = useState(false);
  //#endregion

  // #region FORM
  const { handleSubmit, control, reset, getValues } = useForm<PutSiteComment>({
    defaultValues: useMemo(() => {
      return {
        siteComment: settings.comment || '',
      };
    }, [settings]),
    shouldUnregister: false,
  });
  // #endregion FORM

  // #region POST DATA
  const updateSiteComment = usePost<AxiosResponse, PutSiteComment>(
    (request) => putSiteComment(params.globalSiteId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getSiteSettings', params.globalSiteId],
        exact: true
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      setEdit(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
      setEdit(false);
    }
  );
  // #endregion POST DATA

  // #region HANDLERS
  const handleUpdateSiteComment = (request: PutSiteComment) => {
    updateSiteComment.mutate(request);
  };

  const handleCancel = () => {
    reset();
    setEdit(false);
  };

  const handleEdit = (edited: boolean) => setEdit(edited);

  return (
    <Collapsible label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })} open>
      <form onSubmit={handleSubmit(handleUpdateSiteComment)}>
        <Grid container spacing={6} pb={4} px={isMobile ? 4 : 0}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" color="primary">
              {intl.formatMessage({ id: `${PAGE_NAME_ID}.headingLabel` })}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <ActionButtons
              isDisabled={canEditSiteSettings}
              isEditing={edit}
              onEdit={handleEdit}
              onCancel={handleCancel}
              onSave={handleSubmit(handleUpdateSiteComment)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              dark
              disabled={!edit}
              fullWidth
              multiline
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.inputLabel` })}
              type="text"
              controller={{
                name: 'siteComment',
                control: control,
              }}
            />
          </Grid>
        </Grid>
      </form>
    </Collapsible>
  );
};

export default React.memo(SiteInformation);
